import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { CONTACT, NAV_LINKS } from "@/data/site";
import { Button } from "@/components/ui/button";

const Logo = () => (
  <Link to="/" className="group flex min-w-0 items-center gap-3 sm:gap-4" data-testid="header-logo-link">
    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-brand-navy font-serif text-2xl font-black text-brand-copper transition-transform duration-300 group-hover:-rotate-3 sm:h-14 sm:w-14 sm:text-3xl">
      E
    </span>
    <span className="min-w-0 leading-tight">
      <span className="block font-serif text-2xl font-black tracking-tight sm:text-3xl">Egaal</span>
      <span className="mt-1 block text-[11px] font-extrabold uppercase tracking-[0.16em] text-brand-navy/62 sm:text-xs">
        Tax & Immigration Services
      </span>
    </span>
  </Link>
);

export const Header = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="site-header">
      <div className="section-shell flex h-24 items-center justify-between gap-6">
        <Logo />
        <nav className="hidden items-center gap-7 xl:flex" aria-label="Primary navigation">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.href}
              to={link.href}
              data-testid={`nav-link-${link.label.toLowerCase().replaceAll(" ", "-")}`}
              className={({ isActive }) =>
                `whitespace-nowrap text-sm font-bold transition-colors duration-300 hover:text-brand-copper ${
                  isActive ? "text-brand-copper" : "text-brand-navy/72"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-3 xl:flex">
          <a
            href={CONTACT.phoneHref}
            data-testid="header-call-link"
            className="flex items-center gap-2 whitespace-nowrap text-sm font-extrabold text-brand-navy transition-colors duration-300 hover:text-brand-copper"
          >
            <Phone className="h-4 w-4" />
            {CONTACT.phone}
          </a>
          <Button asChild className="rounded-full bg-brand-navy px-5 text-white hover:bg-brand-ink">
            <Link to="/contact" data-testid="header-consultation-cta">Schedule Consultation</Link>
          </Button>
        </div>
        <button
          type="button"
          data-testid="mobile-menu-toggle-button"
          onClick={() => setOpen(!open)}
          className="grid h-11 w-11 place-items-center rounded-full border border-brand-navy/15 xl:hidden"
          aria-label="Toggle navigation menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="border-t border-brand-navy/10 bg-brand-sand px-5 py-6 xl:hidden"
            aria-label="Mobile navigation"
          >
            <div className="flex flex-col gap-4">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.href}
                  to={link.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replaceAll(" ", "-")}`}
                  className="rounded-lg px-3 py-3 text-base font-bold text-brand-navy hover:bg-brand-cream"
                >
                  {link.label}
                </NavLink>
              ))}
              <Button asChild className="mt-2 rounded-full bg-brand-copper text-brand-navy hover:bg-brand-copper/90">
                <Link to="/contact" onClick={() => setOpen(false)} data-testid="mobile-consultation-cta">
                  Schedule an Immigration Consultation
                </Link>
              </Button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
